import React from 'react';
import { Layout, ConfigProvider } from 'antd';
import css from './App.module.less';
import { Router, Switch, Route, Link, Redirect } from 'react-router-dom';
import 'moment/locale/ru';
import logo from './logo.svg';
import { useStore } from 'effector-react';
import authStore from '@stores/auth';
import localeStore, { localeMap } from '@stores/locale';
import AuthRoute from '@components/AuthRoute/AuthRoute';
import UserBlock from '@components/UserBlock';
import LanguageSwitch from '@components/LanguageSwitch';
import MainMenu from '@components/MainMenu';
import routes from '@config/routes';
import history from '@helpers/history';
import useTranslate from '@hooks/useTranslate';
import ErrorHandler from '@components/ErrorHandler';
import ROLES from '@constants/roles';
import SearchHeader from '@components/SearchHeader';

// Routes
import Login from './routes/Login';
import UserListRoute from './routes/UserListRoute';
import PushListRoute from './routes/PushListRoute';
import PostListRoute from './routes/PostListRoute';
import UserDetail from './routes/UserDetailRoute';
import PostDetail from './pages/PostDetailPage';
import SpamRoute from './routes/SpamRoute';
import CommentsSpamRoute from './routes/CommentsSpamRoute';
import UsersSpamRoute from './routes/UsersSpamRoute';
import GamesRoute from './routes/GamesRoute';
import FeaturingRoute from './routes/FeaturingRoute';
import PromotionRoute from './routes/PromotionRoute';
import PromotionGamesRoute from './routes/PromotionGamesRoute/PromotionGamesRoute';
import TribesRoute from './routes/TribesRoute';
import TribesMembersList from '@pages/TribesPage/TribesMembersList/TribesMembersList';
import { ChatDetail } from '@pages/TribesPage';
import StatusesSpamRoute from './routes/StatusesSpamRoute';
import MentionsOfficialAccountPostRoute from './routes/MentionsOfficialAccountPostRoute';
import MentionsOfficialAccountCommentRoute from './routes/MentionsOfficialAccountCommentRoute';
import ExperimentsListRoute from './routes/ExperimentsListRoute';
import RegistrationIpsRoute from './routes/RegistrationIpsRoute';
import BlockedEmailDomainsRoute from './routes/BlockedEmailDomainsRoute/BlockedEmailDomainsRoute';

const { Header, Content, Footer } = Layout;

const App = () => {
  const auth = useStore(authStore);
  const localeName = useStore(localeStore);
  const isAuth = !!auth.user;
  const content = useTranslate();

  const isAdmin = auth?.user?.role === ROLES.ADMIN;

  const homePagePath = routes.userList.path;

  return (
    <Router history={history}>
      <ConfigProvider locale={localeMap[localeName].locale}>
        <Layout className={css.root}>
          <Header className={css.header}>
            <div className={css.widthContainer}>
              <Link
                to={routes.root.path}
                className={css.logo}
              >
                <img
                  src={logo}
                  alt='Gameram'
                />
              </Link>
              {isAuth ? <MainMenu /> : null}

              {isAuth ? <SearchHeader /> : null}
              <LanguageSwitch />
              {isAuth ? <UserBlock /> : null}
            </div>
          </Header>
          <Content className={css.content}>
            <ErrorHandler>
              <Switch>
                <Route
                  path={routes.login.path}
                  render={() => <Login />}
                  exact
                />

                <AuthRoute
                  path={routes.userList.path}
                  render={() => <UserListRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.userDetail.path}
                  render={() => <UserDetail />}
                />

                <AuthRoute
                  path={routes.postList.path}
                  render={() => <PostListRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.postDetail.path}
                  render={() => <PostDetail />}
                />

                <AuthRoute
                  path={routes.tribes.path}
                  render={() => <TribesRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.chatDetail.path}
                  render={() => <ChatDetail />}
                  exact
                />

                <AuthRoute
                  path={routes.tribesMembers.path}
                  render={() => <TribesMembersList />}
                />

                <AuthRoute
                  path={routes.featuring.path}
                  render={() => <FeaturingRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.promotionCommon.path}
                  render={() => <Redirect to={routes.promotionActivity.path} />}
                  exact
                />

                <AuthRoute
                  path={routes.promotionActivity.path}
                  render={() => <PromotionRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.promotionGames.path}
                  render={() => <PromotionGamesRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.promotionPush.path}
                  render={() => <PushListRoute />}
                  exact
                />

                {isAdmin && (
                  <AuthRoute
                    path={routes.experiments.path}
                    render={() => <ExperimentsListRoute />}
                    exact
                  />
                )}

                {isAdmin && (
                  <AuthRoute
                    path={routes.mentionsOfficialAccountPost.path}
                    render={() => <MentionsOfficialAccountPostRoute />}
                    exact
                  />
                )}

                {isAdmin && (
                  <AuthRoute
                    path={routes.mentionsOfficialAccountComment.path}
                    render={() => <MentionsOfficialAccountCommentRoute />}
                    exact
                  />
                )}

                <AuthRoute
                  path={routes.spamCommon.path}
                  render={() => <Redirect to={routes.spam.path} />}
                  exact
                />

                <AuthRoute
                  path={routes.spamComments.path}
                  render={() => <CommentsSpamRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.spamStatuses.path}
                  render={() => <StatusesSpamRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.spamUsers.path}
                  render={() => <UsersSpamRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.spam.path}
                  render={() => <SpamRoute />}
                  exact
                />

                <AuthRoute
                  path={routes.games.path}
                  render={() => <GamesRoute />}
                  exact
                />

                {isAdmin && (
                  <AuthRoute
                    path={routes.registrationIps.path}
                    render={() => <RegistrationIpsRoute />}
                    exact
                  />
                )}

                {isAdmin && (
                  <AuthRoute
                    path={routes.blockedEmailDomains.path}
                    render={() => <BlockedEmailDomainsRoute />}
                    exact
                  />
                )}

                <Redirect to={homePagePath} />
              </Switch>
            </ErrorHandler>
          </Content>
          <Footer className={css.footer}>
            <div className={css.widthContainer}>{content.copyright}</div>
          </Footer>
        </Layout>
      </ConfigProvider>
    </Router>
  );
};

export default App;
